import React, { useState } from "react";  
import api from "../api";  
import { useNavigate, Link, useLocation } from "react-router-dom";  
import { motion, AnimatePresence } from "framer-motion";  
  
function Login() {  
  const location = useLocation();  
  const navigate = useNavigate();  
  const [form, setForm] = useState({  
    username: location.state?.email || "",  
    password: "",  
  });  
  const [message, setMessage] = useState("");  
  const [messageType, setMessageType] = useState("");  
  const [isLoading, setIsLoading] = useState(false);  
  const [showPassword, setShowPassword] = useState(false);  
  const [showForgotPassword, setShowForgotPassword] = useState(false);  
  const [resetEmail, setResetEmail] = useState("");  
  
  const handleChange = (e) => {  
    setForm({ ...form, [e.target.name]: e.target.value });  
    setMessage("");  
  };  
  
  const handleLogin = async (e) => {  
    e.preventDefault();  
    setIsLoading(true);  
    setMessage("");  
    try {  
      const res = await api.post("/users/login", form);  
      const token = res.data.access_token;  
      const isSuperuser = res.data.is_superuser;  
      localStorage.setItem("access_token", token);  
      localStorage.setItem("is_superuser", isSuperuser);  
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;  
      setMessage("Signed in — redirecting…");  
      setMessageType("success");  
      setTimeout(() => navigate(isSuperuser ? "/admin" : "/dashboard"), 800);  
    } catch (err) {  
      setMessageType("error");  
      if (err.response?.data?.detail) setMessage(err.response.data.detail);  
      else if (err.message === "Network Error") setMessage("Can't reach the server. Check your connection.");  
      else setMessage("Invalid credentials. Please try again.");  
    } finally {  
      setIsLoading(false);  
    }  
  };  
  
  const handleForgotPassword = async (e) => {  
    e.preventDefault();  
    if (!resetEmail) {  
      setMessage("Enter your email first");  
      setMessageType("error");  
      return;  
    }  
    setIsLoading(true);  
    try {  
      await api.post("/users/forgot-password", { email: resetEmail });  
      setMessage("If that email exists, we've sent a reset link.");  
      setMessageType("success");  
      setShowForgotPassword(false);  
      setResetEmail("");  
    } catch (err) {  
      setMessage("Couldn't send the reset email. Try again shortly.");  
      setMessageType("error");  
    } finally {  
      setIsLoading(false);  
    }  
  };  
  
  return (  
    <AuthShell>  
      <motion.div  
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}  
        className="w-full max-w-md"  
      >  
        {/* Compact header */}  
        <div className="mb-6 text-center">  
          <Link to="/" className="inline-flex items-center gap-2 mb-8">  
            <div className="w-8 h-8 bg-gradient-to-br from-gold to-yellow-600 rounded-lg flex items-center justify-center shadow-lg shadow-gold/20">  
              <svg className="w-4 h-4 text-black" fill="currentColor" viewBox="0 0 20 20">  
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />  
              </svg>  
            </div>  
            <span className="text-base font-bold text-white tracking-tight">iTrader</span>  
          </Link>  
          <h1 className="text-2xl sm:text-3xl font-bold text-white">  
            {showForgotPassword ? "Reset your password" : "Welcome back"}  
          </h1>  
          <p className="text-sm text-gray-400 mt-1.5">  
            {showForgotPassword ? "We'll email you a reset link" : "Sign in to continue trading"}  
          </p>  
        </div>  
  
        {/* Card */}  
        <div className="bg-gradient-to-br from-gray-900/60 via-black/60 to-gray-900/60 backdrop-blur-xl border border-gray-800 rounded-2xl p-6 sm:p-7 shadow-2xl">  
          {!showForgotPassword ? (  
            <form onSubmit={handleLogin} className="space-y-4">  
              <FormField  
                label="Username or email"  
                name="username"  
                value={form.username}  
                onChange={handleChange}  
                placeholder="you@example.com"  
                autoComplete="username"  
                disabled={isLoading}  
                required  
              />  
  
              <FormField  
                label="Password"  
                name="password"  
                type={showPassword ? "text" : "password"}  
                value={form.password}  
                onChange={handleChange}  
                placeholder="Enter your password"  
                autoComplete="current-password"  
                disabled={isLoading}  
                required  
                labelTrailing={  
                  <button  
                    type="button"  
                    onClick={() => setShowForgotPassword(true)}  
                    className="text-[11px] font-semibold text-gold hover:text-yellow-300 transition-colors"  
                  >  
                    Forgot?  
                  </button>  
                }  
                trailing={  
                  <button  
                    type="button"  
                    onClick={() => setShowPassword(!showPassword)}  
                    className="text-gray-500 hover:text-gold transition-colors"  
                    tabIndex={-1}  
                  >  
                    <EyeIcon shown={showPassword} />  
                  </button>  
                }  
              />  
  
              <SubmitButton loading={isLoading} label="Sign in" loadingLabel="Signing in…" />  
  
              <p className="text-center text-xs text-gray-400 pt-1">  
                Don't have an account?{" "}  
                <Link to="/register" className="text-gold hover:text-yellow-300 font-semibold transition-colors">  
                  Create one  
                </Link>  
              </p>  
            </form>  
          ) : (  
            <form onSubmit={handleForgotPassword} className="space-y-4">  
              <FormField  
                label="Email address"  
                type="email"  
                value={resetEmail}  
                onChange={(e) => setResetEmail(e.target.value)}  
                placeholder="you@example.com"  
                disabled={isLoading}  
                required  
              />  
              <div className="flex gap-2 pt-1">  
                <button  
                  type="button"  
                  onClick={() => setShowForgotPassword(false)}  
                  className="flex-1 py-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-gray-800 hover:border-gray-700 text-gray-300 hover:text-white text-sm font-semibold transition-all"  
                >  
                  Back  
                </button>  
                <SubmitButton loading={isLoading} label="Send reset link" loadingLabel="Sending…" className="flex-1" />  
              </div>  
            </form>  
          )}  
  
          <MessageBanner message={message} type={messageType} />  
        </div>  
      </motion.div>  
    </AuthShell>  
  );  
}  
  
/* ─────────────────────────────────────────── */  
/* Shared shell (used by Login + Register)     */  
/* ─────────────────────────────────────────── */  
export const AuthShell = ({ children }) => (  
  <div className="min-h-screen bg-black text-white relative overflow-hidden flex items-center justify-center px-4 py-10">  
    <div className="absolute inset-0 overflow-hidden pointer-events-none">  
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-gold/10 rounded-full blur-3xl animate-pulse" />  
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-yellow-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "2s" }} />  
    </div>  
    <div  
      className="absolute inset-0 opacity-[0.03] pointer-events-none"  
      style={{  
        backgroundImage:  
          "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",  
        backgroundSize: "60px 60px",  
      }}  
    />  
    <div className="relative z-10 w-full flex justify-center">{children}</div>  
  </div>  
);  
  
/* ─────────────────────────────────────────── */  
/* Shared form primitives                      */  
/* ─────────────────────────────────────────── */  
export const FormField = ({  
  label, name, value, onChange, type = "text", placeholder,  
  disabled, required, autoComplete, trailing, labelTrailing, error, helper,  
}) => (  
  <div>  
    <div className="flex items-center justify-between mb-2">  
      <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</label>  
      {labelTrailing}  
    </div>  
    <div className="relative">  
      <input  
        type={type}  
        name={name}  
        value={value}  
        onChange={onChange}  
        placeholder={placeholder}  
        disabled={disabled}  
        required={required}  
        autoComplete={autoComplete}  
        className={`w-full px-4 py-3 bg-black/40 border rounded-xl text-white text-sm placeholder-gray-600 outline-none transition-all ${  
          error ? "border-red-500/60 focus:border-red-500" : "border-gray-800 focus:border-gold/50 focus:ring-1 focus:ring-gold/30"  
        } ${trailing ? "pr-11" : ""}`}  
      />  
      {trailing && <div className="absolute right-3 top-1/2 -translate-y-1/2">{trailing}</div>}  
    </div>  
    {error && (  
      <p className="text-red-400 text-[11px] mt-1.5 flex items-center gap-1">  
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">  
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />  
        </svg>  
        {error}  
      </p>  
    )}  
    {helper && !error && <p className="text-[11px] text-gray-500 mt-1.5">{helper}</p>}  
  </div>  
);  
  
export const SubmitButton = ({ loading, label, loadingLabel, className = "" }) => (  
  <motion.button  
    type="submit"  
    disabled={loading}  
    whileHover={{ scale: loading ? 1 : 1.01 }}  
    whileTap={{ scale: loading ? 1 : 0.99 }}  
    className={`w-full py-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${  
      loading  
        ? "bg-gray-800 text-gray-500 cursor-not-allowed"  
        : "bg-gradient-to-r from-gold to-yellow-600 text-black hover:from-yellow-500 hover:to-gold shadow-lg shadow-gold/20"  
    } ${className}`}  
  >  
    {loading ? (  
      <>  
        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />  
        {loadingLabel}  
      </>  
    ) : (  
      <>  
        {label}  
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">  
          <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />  
        </svg>  
      </>  
    )}  
  </motion.button>  
);  
  
export const MessageBanner = ({ message, type }) => (  
  <AnimatePresence>  
    {message && (  
      <motion.div  
        initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}  
        className={`mt-4 p-3.5 rounded-xl border ${  
          type === "success" ? "bg-green-900/20 border-green-500/40" : "bg-red-900/20 border-red-500/40"  
        }`}  
      >  
        <div className="flex items-start gap-2.5">  
          {type === "success" ? (  
            <svg className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">  
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />  
            </svg>  
          ) : (  
            <svg className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">  
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />  
            </svg>  
          )}  
          <p className={`text-xs font-medium ${type === "success" ? "text-green-300" : "text-red-300"}`}>{message}</p>  
        </div>  
      </motion.div>  
    )}  
  </AnimatePresence>  
);  
  
export const EyeIcon = ({ shown }) =>  
  shown ? (  
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">  
      <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />  
      <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />  
    </svg>  
  ) : (  
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">  
      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />  
      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />  
    </svg>  
  );  
  
export default Login;  